import React from "react";
import Slider from "react-slick";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";

import MHButton from "../components/Common/Button/MHButton";

import { ReactComponent as LeftBtn } from "../static/svg/left-btn.svg";
import { ReactComponent as RightBtn } from "../static/svg/right-btn.svg";

import useMediaQuery from "@mui/material/useMediaQuery";
import { styled, useTheme } from "@mui/material/styles";

import useDialog from "../hooks/use-dialog";

import { useHistory } from "react-router-dom";
import useTitle from "../hooks/use-title";

import { formatAmount } from "../utils/utils";
import { InstituteGrid } from "../components/SubComponent/InstituteGrid";
import InstituteApproachSection from "../components/SubComponent/InstituteApproachSection";
import MHSubscribe from "../components/Layout/MHSubscribe";
import MHFooter from "../components/Layout/MHFooter";

type ArrowProps = {
  onClick?: (e: React.MouseEvent) => void;
};

function SampleNextArrow(props: ArrowProps) {
  return (
    <Box className="absolute top-[30%] z-10 lg:-right-8 md:-right-6">
      <RightBtn className="cursor-pointer" onClick={props.onClick} />
    </Box>
  );
}

function SamplePrevArrow(props: ArrowProps) {
  return (
    <Box className="absolute top-[30%] z-10 -left-8">
      <LeftBtn className="cursor-pointer" onClick={props.onClick} />
    </Box>
  );
}

const CONTENT = [
  {
    imageSrc:
      "https://mh-resources-production.s3.us-west-1.amazonaws.com/childcare-lab-5.jpg",
    title: "Increase their visibility and attract more families.",
    description:
      "Learn proven strategies to stand out, gain trust, and enroll more children.",
  },
  {
    imageSrc:
      "https://mh-resources-production.s3.us-west-1.amazonaws.com/childcare-lab-6.jpg",
    title: "Build a profitable and sustainable childcare business.",
    description:
      "Master financial planning, pricing, and growth strategies for long-term success in childcare.",
  },
  {
    imageSrc:
      "https://mh-resources-production.s3.us-west-1.amazonaws.com/childcare-lab-7.jpg",
    title: "Deliver exceptional care while improving operations.",
    description:
      "Enhance learning environments, streamline processes, and provide top-tier childcare services efficiently.",
  },
  // {
  //   imageSrc:
  //     "https://mh-resources-production.s3.us-west-1.amazonaws.com/Care+Support2.png",
  //   title: "24/7 Care Concierge Service",
  //   description:
  //     "As a member of Caring Blocks, you'll have access to our 24/7 care concierge service. Whether you need support with your job or have a concern that needs addressing, we're here to help at any time, day or night.",
  // },
  // {
  //   imageSrc:
  //     "https://mh-resources-production.s3.us-west-1.amazonaws.com/16.png",
  //   title: "Safe and Trusted Work Environment",
  //   description:
  //     "We prioritize your safety and well-being. All families you are matched with have undergone thorough vetting, and we ensure a respectful and professional work environment. Your privacy and security are of utmost importance to us.",
  // },
];

const APPROACH = [
  {
    label: "Advocating for Paid Family & Medical Leave",
    text: "Women need time to heal, recover, and care for their own health without financial penalties.",
  },
  {
    label: "Healthcare Policies That Recognize the Impact of Care Work",
    text: "Research and policy advocacy to ensure that caregiving-related health challenges are included in national health plans.",
  },
  {
    label: "Mental Health Support for Caregivers",
    text: "Breaking the stigma around depression, stress, and burnout in caregiving, and creating access to affordable mental health resources.",
  },
  {
    label: "Workplace Care Solutions",
    text: "Encouraging employer-sponsored childcare, parental leave, and flexible work policies that allow women to balance caregiving without sacrificing their careers or health.",
  },
  {
    label: "Accessible & Affordable Healthcare for Care Workers",
    text: "Ensuring that domestic workers, nannies, nurses, and caregivers—who support families and economies—have access to healthcare, insurance, and support services.",
  },
];

const WomenHealthPage = () => {
  const {
    openDialog: open,
    handleOpenDialog: handleOpen,
    handleCloseDialog,
  } = useDialog();

  const handleClose = () => {
    handleCloseDialog();
  };

  const trainingCost = process.env.REACT_APP_CHILCARE_LAB_TRAINING_COST;

  const settings = {
    centerMode: true,
    centerPadding: "0px",
    dots: false,
    infinite: true,
    slidesToShow: 4,
    slidesToScroll: 1,
    speed: 500,
    autoplay: false,
    // autoplaySpeed: 1400,
    nextArrow: <SampleNextArrow />,
    prevArrow: <SamplePrevArrow />,
  };

  const theme = useTheme();
  const matchesMdDown = useMediaQuery(theme.breakpoints.down("md"));

  const history = useHistory();

  const handleCareRoute = () => {
    window.open("https://motherhonestly.typeform.com/cbcaregivers");
  };

  useTitle("For Women Health");

  return (
    <React.Fragment>
      <Grid container direction={{ xs: "column-reverse", md: "row" }} mb={4}>
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              "& > img": {
                height: 600,
                width: "100%",
                objectPosition: "center",
                objectFit: "cover",
              },
            }}
          >
            <img
              src="https://mh-resources-production.s3.us-west-1.amazonaws.com/The+Overlooked+Health+Crisis+Among+Women+Who+Care.jpg"
              // src="https://mh-resources-production.s3.us-west-1.amazonaws.com/childcare-lab-4.jpg"
              alt="Banner"
              width="700"
              draggable={false}
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={6} bgcolor="#F3F4F6">
          <Stack
            height={600}
            justifyContent="center"
            alignItems="center"
            px={{ xs: 2, md: 10 }}
            sx={{
              "& > p": {
                textAlign: "center",
                pointerEvents: "none",
              },
            }}
          >
            <Typography
              variant="body1"
              fontFamily="Area-Extended"
              textTransform="uppercase"
              color="#194049"
              fontSize={10}
              letterSpacing="0.1em"
              lineHeight="122%"
              width="60%"
              sx={{
                mb: 3,
              }}
              paragraph
            >
              WOMEN'S HEALTH
            </Typography>

            <Typography
              variant="h1"
              fontSize={{ xs: "30px", sm: "35px", md: "38px" }}
              textTransform="capitalize"
              width="90%"
              sx={{
                mb: 4,
              }}
              paragraph
            >
              The Overlooked Health Crisis Among Women Who Care
            </Typography>

            <Typography
              variant="body1"
              align="center"
              color="#194049"
              width="85%"
              sx={{
                mb: 7,
              }}
              paragraph
            >
              Women in Africa are not just caregivers; they are the backbone of
              families, workplaces, and communities. They{" "}
              <b className=" font-areaBlack">provide 80% of all care work</b>
              —from raising children to tending to aging parents, supporting
              extended families, and even filling the gaps in underfunded
              healthcare systems. But this{" "}
              <b className=" font-areaBlack">
                relentless cycle of caregiving comes at a cost
              </b>
              —a cost that is rarely acknowledged in our healthcare policies,
              economic strategies, or gender equity frameworks. The impact of{" "}
              <b className=" font-areaBlack">
                unpaid and underpaid care work on women's health
              </b>{" "}
              is undeniable, yet it remains{" "}
              <b className=" font-areaBlack">invisible</b>.
            </Typography>

            {/* <MHButton
              sx={{
                width: "fit-content",
              }}
              // onClick={handleCareRoute}
              onClick={() => {
                const element = document.getElementById("childcareLabReg");
                if (element) {
                  element.scrollIntoView({ behavior: "smooth" });
                }
              }}
            >
              Start Your Application
            </MHButton> */}
          </Stack>
        </Grid>
      </Grid>

      <Box
        width={{ xs: "90%", md: "100%" }}
        mx="auto"
        pt={5}
        justifyContent="center"
        // sx={{ backgroundColor: "#f6f6f6" }}
        className="flex place-content-center"
      >
        <Typography
          variant="body1"
          fontFamily="Area-Extended"
          textTransform="uppercase"
          color="#194049"
          fontSize={{ xs: "12px", sm: "14px" }}
          letterSpacing="0.1em"
          lineHeight="122%"
          // width="100%"
          sx={{
            mb: 5,
            textAlign: "center",
          }}
          paragraph
        >
          It's Time We Care for the Caregivers
        </Typography>
      </Box>

      {/* <Typography
        variant="h1"
        color="primary"
        fontSize={{ xs: "26px", sm: "32px" }}
        lineHeight={{ xs: "143%", sm: "120.5%" }}
        letterSpacing={"-0.04em"}
        className="w-[302px] md:w-[560px] mx-auto text-center"
      >
        Workshop Overview
      </Typography> */}

      <Box my={4}>
        <InstituteGrid
          title="The Health Implications Of Caregiving In Africa"
          content="African women are facing a silent health crisis"
          objective={[
            {
              caption: ``,
              info: `African women are facing a silent health crisis driven by their overwhelming care responsibilities. The physical strain of caregiving—lifting, carrying, and standing for long hours—leads to musculoskeletal disorders, chronic fatigue, and long-term health conditions. Mental and emotional burnout is widespread, with many women experiencing depression, anxiety, and stress-related illnesses as they juggle caregiving, work, and financial insecurity. `,
            },
          ]}
          // imageSrc="https://mh-resources-production.s3.us-west-1.amazonaws.com/Health+Assessment.png"
          imageSrc="https://mh-resources-production.s3.us-west-1.amazonaws.com/The+Health+Implications+Of+Caregiving+In+Africa.jpg"
          // inverted
        />
      </Box>
      <Box my={4}>
        <InstituteGrid
          title="The Link Between Caregiving and Health"
          content="Women often delay or deny their own healthcare"
          objective={[
            {
              caption: ``,
              info: `Women often delay or deny their own healthcare, prioritizing everyone else's well-being over their own, skipping doctor's appointments, neglecting preventive care, and managing chronic conditions without adequate medical support. The burden extends to maternal and reproductive health, where inadequate maternity leave policies, the absence of universal childcare, and the mental load of care work heighten risks for pregnancy complications, postpartum depression, and long-term hormonal imbalances. Despite carrying so much, women's health remains an afterthought in economic and policy discussions—leaving them vulnerable in systems that depend on their unpaid labor.`,
            },
          ]}
          imageSrc="https://mh-resources-production.s3.us-west-1.amazonaws.com/The+Link+Between+Caregiving+and+Health.jpg"
          inverted
        />
      </Box>
      <Box my={4}>
        <InstituteGrid
          title="The Economic & Societal Cost of Ignoring Women's Health"
          content="when caregivers are unhealthy, children's well-being declines"
          objective={[
            {
              caption: ``,
              info: `When women's health is compromised, entire households, communities, and economies suffer. Research shows that when caregivers are unhealthy, children's well-being declines, workplaces become less productive, and healthcare costs rise. The economic impact of women exiting the workforce due to caregiving responsibilities and health decline is staggering. It is time to redefine women's health beyond reproductive care and acknowledge the full scope of how caregiving affects their physical, mental, and economic well-being.`,
            },
          ]}
          imageSrc="https://mh-resources-production.s3.us-west-1.amazonaws.com/The+Economic+%26+Societal+Cost+of+Ignoring+Women's+Health.jpg"
          // inverted
        />
      </Box>

      <InstituteApproachSection
        header="Our Commitment"
        text="A Holistic Approach to Women's Health & Caregiving"
        content=" At Caring Africa, we are committed to advocating for a care-centered approach to women’s health—one that integrates healthcare, workplace policies, and caregiving solutions to support African women in every stage of their lives. We Focus on:"
        data={APPROACH}
      />

      <Box
        width={{ xs: "90%", sm: "100%" }}
        mx="auto"
        py={5}
        sx={{ backgroundColor: "#f6f6f6" }}
      >
        <Typography
          variant="h1"
          fontSize={{ xs: "30px", sm: "40px", md: "50px" }}
          textTransform="capitalize"
          align="center"
          sx={{
            mb: 4,
          }}
          paragraph
        >
          {/* extend care beyond the company’s walls, creating a win-win scenario
          where you and <br /> your employees thrive and prosper. */}
          Join Us in Building a Future Where <br /> Women's Health & Care Matter
        </Typography>
        <Typography
          variant="body1"
          color="primary.main"
          width={{ xs: "100%", md: "70%" }}
          fontSize="14px"
          lineHeight="185%"
          letterSpacing="0.02em"
          align="center"
          mx="auto"
          paragraph
        >
          Women's health is not a{" "}
          <b className=" font-areaBlack"> privilege—it is a right</b>. When we
          invest in{" "}
          <b className=" font-areaBlack">
            policies, healthcare solutions, and economic structures that support
            women,
          </b>{" "}
          we build a stronger, healthier Africa for everyone.
          <br />
          <br />
          It's time to stop{" "}
          <b className=" font-areaBlack">
            seeing caregiving as an individual burden{" "}
          </b>{" "}
          and start recognizing it as{" "}
          <b className=" font-areaBlack">
            a critical health and economic issue
          </b>
          .
        </Typography>

        {/* <MHButton
          sx={{
            width: "fit-content",
            display: "block",
            mx: "auto",
            mt: 5,
          }}
          onClick={handleCareRoute}
        >
          Start Your Application
        </MHButton> */}
      </Box>

      {/* <InstitutesHeroSlider
        data={CONTENT}
        header="Who Should Attend?"
        content={`Childcare providers, daycare owners, and educators who want to:`}
      /> */}

      <MHSubscribe />
      <MHFooter />
    </React.Fragment>
  );
};

export default WomenHealthPage;
